import React from "react";
import { X, FileText, Calendar, Package, CheckCircle, XCircle } from "lucide-react";

const ViewFormModal = ({ isOpen, onClose, request, formType }) => {
  if (!isOpen || !request) return null;

  const formatLabel = (key) => {
    const overrides = {
      rfqTitle: "RFQ Title",
      referenceNumber: "Reference Number",
      buyerContact: "Buyer Contact",
      RFQId: "RFQ ID",
      issueDate: "Issue Date",
      submissionDeadline: "Submission Deadline",
      projectName: "Project Name",
      notes: "Notes",
      status: "Status",
    };

    if (overrides[key]) return overrides[key];

    return key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
      approved: "bg-green-100 text-green-800 border-green-300",
      rejected: "bg-red-100 text-red-800 border-red-300",
      completed: "bg-orange-100 text-orange-800 border-orange-300",
      draft: "bg-gray-100 text-gray-800 border-gray-300",
    };
    return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-300";
  };

  const renderItemsTable = () => {
    const items = request.lineItems || request.items || request.boqItems || [];

    if (!items.length) return null;

    return (
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-4">
          <Package className="w-5 h-5 text-orange-600" />
          <h3 className="text-lg font-semibold text-gray-800">Line Items</h3>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-orange-50 to-amber-50">
              <tr>
                {Object.keys(items[0]).map((key) => (
                  <th key={key} className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {formatLabel(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((item, idx) => (
                <tr key={idx} className="hover:bg-orange-50 transition-colors">
                  {Object.keys(item).map((key) => (
                    <td key={key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {key.toLowerCase().includes("date")
                        ? new Date(item[key]).toLocaleDateString()
                        : item[key] || "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderFiles = () => {
    if (!request.files || request.files.length === 0) return null;

    return (
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-orange-600" />
          <h3 className="text-lg font-semibold text-gray-800">Attached Files</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {request.files.map((file, idx) => (
            <a
              key={idx}
              href={file}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-orange-50 border border-gray-200 hover:border-orange-300 rounded-lg transition-all group"
            >
              <FileText className="w-5 h-5 text-gray-400 group-hover:text-orange-600 transition-colors" />
              <span className="text-sm text-gray-700 group-hover:text-orange-600 truncate">
                {file.split("/").pop()}
              </span>
            </a>
          ))}
        </div>
      </div>
    );
  };

  const renderDetails = () => {
    const excludeKeys = [
      "lineItems",
      "items",
      "boqItems",
      "files",
      "__v",
      "createdAt",
      "updatedAt",
      "_id",
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.keys(request)
          .filter((key) => !excludeKeys.includes(key))
          .map((key) => {
            const value = request[key];

            if (key === "attachments" && Array.isArray(value)) {
              return (
                <div key={key} className="col-span-full">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    {formatLabel(key)}
                  </label>
                  <div className="space-y-2">
                    {value.map((att, idx) => (
                      <div
                        key={att._id || idx}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-800">{att.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${att.required ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                            {att.required ? "Required" : "Optional"}
                          </span>
                          {att.provided ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            if (Array.isArray(value)) {
              return (
                <div key={key}>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {formatLabel(key)}
                  </label>
                  <p className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg border border-gray-200">
                    {value.length > 0 ? value.join(", ") : "-"}
                  </p>
                </div>
              );
            }

            if (typeof value === "object" && value !== null) {
              return (
                <div key={key} className="col-span-full">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {formatLabel(key)}
                  </label>
                  <pre className="text-xs text-gray-800 bg-gray-50 p-4 rounded-lg border border-gray-200 overflow-x-auto">
                    {JSON.stringify(value, null, 2)}
                  </pre>
                </div>
              );
            }

            if (key.toLowerCase() === "status") {
              return (
                <div key={key}>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {formatLabel(key)}
                  </label>
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(value)}`}>
                    {value || "-"}
                  </span>
                </div>
              );
            }

            return (
              <div key={key}>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {formatLabel(key)}
                </label>
                <p className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg border border-gray-200">
                  {key.toLowerCase().includes("date")
                    ? new Date(value).toLocaleDateString()
                    : value || "-"}
                </p>
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-400 to-amber-600 px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-7 h-7 text-white" />
            <h2 className="text-2xl font-bold text-white">
              {formType || "Form"} Details
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-8">
          {renderDetails()}
          {renderItemsTable()}
          {renderFiles()}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-8 py-4 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="bg-white hover:bg-gray-100 text-gray-700 px-6 py-2.5 rounded-lg font-medium border border-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewFormModal;